import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { PortalLoan } from '@willow/graphql-iso/src/portal';
import { Loader, paymentAmountFormat } from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { LoanId, RepayAccountId } from '@willow/types-iso';

import { AutopayContext } from '../../contexts/autopayContext';
import { WillowModal } from '../modal/Modal';

import './AutopayModal.scss';

interface ConfirmAutopayModalProps {
  loanId: LoanId;
  pastDueBalance: number | undefined;
  repayAccounts: PortalLoan['repayAccounts'];
}

export const ConfirmRepayAutopayModal = ({ loanId, pastDueBalance, repayAccounts }: ConfirmAutopayModalProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<RepayAccountId | undefined>(repayAccounts.at(0)?.id);
  const {
    showConfirmAutopayModal,
    setShowConfirmAutopayModal,
    updateRepayAutopaySettings: updateAutopaySettingsRepay,
    updateRepayAutopaySettingsLoading,
  } = useContext(AutopayContext);

  useEffect(() => {
    // When user adds a repay account, set the first account as the default
    if (selectedAccountId == null && repayAccounts.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedAccountId(repayAccounts.at(0)?.id);
    }
  }, [repayAccounts, repayAccounts.length, selectedAccountId]);

  const resetModal = () => {
    setShowConfirmAutopayModal(false);
  };

  const onSubmit = async () => {
    try {
      await updateAutopaySettingsRepay(loanId, selectedAccountId);
      resetModal();
      toast.success('Auto-pay was successfully enabled');
    } catch (_err: unknown) {
      toast.error('There was an issue updating auto-pay. Please refresh the page and try again.');
    }
  };

  return (
    <WillowModal showModal={showConfirmAutopayModal} onClose={resetModal}>
      {repayAccounts.length ? (
        <div className="modal-autopay">
          <h2>Enable Auto-Pay?</h2>

          <div className="modal-autopay__copy">
            {repayAccounts.length > 1 && (
              <Form.Group>
                <Form.Label>Confirm bank account to enroll in auto-pay.</Form.Label>
                <Form.Select
                  onChange={(e) => setSelectedAccountId(e.target.value as RepayAccountId)}
                  value={selectedAccountId}
                >
                  {repayAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.accountName} ({account.lastFour})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {pastDueBalance ? (
              <>
                <p className="mb-4">
                  Your loan is currently past due and {paymentAmountFormat(pastDueBalance)} is outstanding. You can
                  confirm immediate payment by pressing the “submit payment” button on the payment page.
                </p>
                <p>
                  Enabling auto-pay will deduct any remaining unpaid balance (including any fees or past due amount) due
                  on your next due date.
                </p>
              </>
            ) : (
              <>
                <p className="mb-4">
                  Enabling auto-pay will deduct any remaining unpaid balance (including any fees or past due amount) due
                  on your due date.
                </p>
                <p>
                  You can still submit a payment manually by confirming the amount you wish to pay and pressing the
                  “submit payment” button. Any remaining amount will be auto-deducted on your due date if auto-payment
                  is enabled.
                </p>
              </>
            )}
          </div>

          <div className="modal-autopay__buttons">
            <button
              type="button"
              onClick={onSubmit}
              className="modal-autopay__submit-btn"
              disabled={updateRepayAutopaySettingsLoading || !selectedAccountId}
            >
              Enable Auto-Pay
              {updateRepayAutopaySettingsLoading && <Loader />}
            </button>
            <button type="button" onClick={resetModal} className="modal-autopay__cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="modal-autopay">
          <h2>Authorization Required</h2>

          <div className="modal-autopay__copy">
            New authorization is required that was not previously obtained while connecting your bank account(s).{' '}
            <b>Please delete and re-connect your connected bank account(s) to enable auto-pay.</b>
          </div>

          <div className="modal-autopay__buttons">
            <button type="button" onClick={resetModal} className="modal-autopay__submit-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </WillowModal>
  );
};
